import React, { FunctionComponent } from 'react';
import { Autocomplete, TextField, Typography, Chip, useTheme } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import AuthChip, { getAuthTypeConfig } from './AuthChip';
import StabilityChip, { getStabilityConfig } from './StabilityChip';
import useOpenAPIFilteredOperations from '../hooks/useOpenAPIFilteredOperations';
import useFilterBarStyles from './FilterBar.styles';
import { FilterOption, FilterOptionType } from '../utils/filterUtils';

const FilterBar: FunctionComponent = () => {
  const { classes } = useFilterBarStyles();
  const { translate } = useTranslation();
  const theme = useTheme();
  const { filterOptions, selectedFilterOptions, setSelectedFilterOptions } =
    useOpenAPIFilteredOperations();

  const getFilterOptionLabelKey = (option: FilterOption): string | null => {
    if (option.type === FilterOptionType.Auth) {
      const config = getAuthTypeConfig(option.value, theme);
      return config?.labelKey || null;
    }
    if (option.type === FilterOptionType.Stability) {
      const config = getStabilityConfig(option.value, theme);
      return config?.labelKey || null;
    }
    return null;
  };

  const getFilterGroupLabelKey = (option: FilterOption): string => {
    switch (option.type) {
      case FilterOptionType.Stability:
        return 'Label.StabilityLevels';
      case FilterOptionType.Auth:
        return 'Label.AuthenticationTypes';
      case FilterOptionType.Domain:
        return 'Label.Domains';
      default:
        return '';
    }
  };

  const renderFilterGroup = (params: {
    group: string;
    key: string;
    children?: React.ReactNode;
  }) => (
    <li key={params.key}>
      <div className={classes.groupHeader}>
        <Typography variant='tableHead' component='div' color='secondary'>
          {params.group}
        </Typography>
      </div>
      <ul className={classes.groupItems}>{params.children}</ul>
    </li>
  );

  const renderFilterOptionContent = (option: FilterOption): React.ReactNode => {
    if (option.type === FilterOptionType.Auth) {
      return <AuthChip authType={option.value} tooltipPlacement='right' />;
    }
    if (option.type === FilterOptionType.Stability) {
      return <StabilityChip stability={option.value} tooltipPlacement='right' />;
    }
    if (option.type === FilterOptionType.Domain) {
      return <Typography variant='body2'>{option.value}</Typography>;
    }
    return null;
  };

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: FilterOption) => (
    <li {...props} key={`${option.type}:${option.value}`}>
      {renderFilterOptionContent(option)}
    </li>
  );

  const renderSelectedFilterOptions = (
    tagValue: FilterOption[],
    getTagProps: (params: { index: number }) => {
      key: number;
      className: string;
      disabled: boolean;
      'data-tag-index': number;
      tabIndex: number;
      onDelete: (event: unknown) => void;
    },
  ) =>
    tagValue.map((option, index) => {
      const { key, className: existingClassName, ...restTagProps } = getTagProps({ index });
      const labelKey = getFilterOptionLabelKey(option);
      const label = labelKey ? translate(labelKey) : option.value;

      // Build chip props
      const chipProps: React.ComponentProps<typeof Chip> = {
        label,
        size: 'small' as const,
        variant: 'outlined' as const,
        color: 'primary' as const,
        className: existingClassName,
        ...restTagProps,
      };

      // Add style and icon if config exists
      const authConfig =
        option.type === FilterOptionType.Auth ? getAuthTypeConfig(option.value, theme) : null;
      if (authConfig) {
        chipProps.style = { color: authConfig.color };
        if (authConfig.IconComponent) {
          chipProps.icon = (
            <authConfig.IconComponent
              fontSize='small'
              style={{ color: authConfig.color, width: '12px' }}
            />
          );
        }
      }

      const stabilityConfig =
        option.type === FilterOptionType.Stability ? getStabilityConfig(option.value, theme) : null;
      if (stabilityConfig) {
        chipProps.style = { ...chipProps.style, borderColor: stabilityConfig.color };
      }

      return (
        <React.Fragment key={key}>
          <Chip {...chipProps} />
        </React.Fragment>
      );
    });

  return (
    <Autocomplete
      autoHighlight
      multiple
      options={filterOptions}
      value={selectedFilterOptions}
      onChange={(_event, newValue) => setSelectedFilterOptions(newValue)}
      groupBy={(option: FilterOption) => translate(getFilterGroupLabelKey(option))}
      renderGroup={renderFilterGroup}
      renderOption={renderOption}
      renderTags={renderSelectedFilterOptions}
      getOptionLabel={(option) => {
        const labelKey = getFilterOptionLabelKey(option);
        return labelKey ? translate(labelKey) : option.value;
      }}
      isOptionEqualToValue={(option, value) =>
        option.type === value.type && option.value === value.value
      }
      renderInput={(params) => (
        <TextField {...params} label={translate('Label.Filter')} size='small' variant='outlined' />
      )}
      size='small'
      classes={{ root: classes.autocomplete }}
    />
  );
};

export default FilterBar;
